import { Background, Controls, Edge, Node, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useEffect, useState } from 'react';

import { ExpandableSection } from '@cloudscape-design/components';
import { fetchItemsByIds, fetchItemsWithOrFilters } from '../api/generic';
import { GraphLink } from '../models/GraphLink';
import InventoryItem from '../models/Inventory';
import { Invoice } from '../models/Invoice';
import { prefixCollection } from '../utils/prefix';
import {
  buildBatchNodes,
  buildInventoryMap,
  buildInvoiceAndInventoryNodesFlat,
  buildLossAndReleaseNodes,
} from './flowHelpers';
import InvoiceNode from './NodeTypes/InvoiceNode';
import { useModuleTranslation } from '../lang';

interface BatchGraphViewProps {
  tenant: string;
  batchId: string;
  batchName: string;
}

const BatchGraphView = ({ tenant, batchId, batchName }: BatchGraphViewProps) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  const load = async () => {
    const batchLinks = await fetchItemsWithOrFilters<GraphLink>(prefixCollection('graphLinks', tenant), {
      or: [
        { field: 'from', operator: '==', value: batchId },
        { field: 'to', operator: '==', value: batchId },
      ],
    });

    const invoiceLinks = batchLinks.filter((link) => link.type.includes('invoice'));
    const invoiceIds = invoiceLinks.map((link) => (link.from === batchId ? link.to : link.from));

    const invoices = await fetchItemsByIds<Invoice>(prefixCollection('invoices', tenant), invoiceIds);

    if (!invoices.length) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const inventoryLinks = await fetchItemsWithOrFilters<GraphLink>(prefixCollection('graphLinks', tenant), {
      or: invoiceIds.flatMap((id) => [
        { field: 'from', operator: '==', value: id },
        { field: 'to', operator: '==', value: id },
      ]),
    });

    const filteredInventoryLinks = inventoryLinks.filter(
      (link) => link.type.includes('inventory') && (invoiceIds.includes(link.from) || invoiceIds.includes(link.to)),
    );

    const inventoryIds = filteredInventoryLinks.map((link) => (invoiceIds.includes(link.from) ? link.to : link.from));

    const inventoryItems = await fetchItemsByIds<InventoryItem>(prefixCollection('inventory', tenant), inventoryIds);

    const inventoryMap = buildInventoryMap(invoiceIds, filteredInventoryLinks, inventoryItems);

    const { invoiceAndInventoryNodes, invoiceAndInventoryEdges, totalHeight } = buildInvoiceAndInventoryNodesFlat(
      invoices,
      inventoryMap,
      batchId,
    );

    const batchCenterY = totalHeight / 2;
    const batchNodes = buildBatchNodes(batchId, batchName, batchCenterY);
    const { nodes: lrNodes, edges: lrEdges } = buildLossAndReleaseNodes(batchId, batchCenterY);

    setNodes([...invoiceAndInventoryNodes, ...batchNodes, ...lrNodes]);
    setEdges([...invoiceAndInventoryEdges, ...lrEdges]);
  };

  useEffect(() => {
    load();
  }, [tenant, batchId, batchName]);

  const [expanded, setExpanded] = useState(false);
  const t = useModuleTranslation('BatchGraphView');

  return (
    <div style={{ marginTop: 10 }}>
      <ExpandableSection
        variant="container"
        headerText={t.header}
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
      >
        <div
          style={{
            height: '600px',
            border: '1px solid #ccc',
            borderRadius: 8,
            marginTop: 10,
          }}
        >
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={{ invoice: InvoiceNode }} fitView>
            <Background />
            <Controls />
          </ReactFlow>
        </div>
      </ExpandableSection>
    </div>
  );
};

export default BatchGraphView;
